<script setup lang="ts">
    import { CardTitle } from '@/components/ui/card';
    import { columns } from '@/tables/columns/power-bi-setup';
    import { computed } from 'vue';
    import { Head } from '@inertiajs/vue3';
    import { usePage } from '@inertiajs/vue3';
    import { usePermissions } from '@/composables/usePermissions';
    import { useQuery } from '@tanstack/vue-query';
    import AppLayout from '@/layouts/AppLayout.vue';
    import axios from 'axios';
    import CreateBIReport from '@/components/power-bi/CreateBIReport.vue';
    import DataTable from '@/components/datatable/DataTable.vue';
    import DataTableToolbarActions from '@/components/datatable/DataTableToolbarActions.vue';
    import PageCard from '@/components/PageCard.vue';
    import Permission from '@/enums/Permission';
    import QueryKey from '@/enums/QueryKey';
    import type { SharedData, BreadcrumbItem, User } from '@/types';
    import UploadBiReport from '@/components/power-bi/UploadBiReport.vue';

    const { hasPermission } = usePermissions();
    const page = usePage<SharedData>();
    const selectedCompanyId = computed(() => page.props.auth.user.selected_company_id);

    const breadcrumbs: BreadcrumbItem[] = [
        {
            title: 'menu.power_bi',
        },
        {
            title: 'menu.power_bi_mapping',
            href: route('v2.powerbi.setup.index'),
        },
    ];

    const { isPending, isFetching, isError, data, error, refetch } = useQuery({
        queryKey: [QueryKey.POWER_BI, selectedCompanyId],
        queryFn: getPowerBiSetup,
    });

    async function getPowerBiSetup() {
        const { data } = await axios.get(route('v2.powerbi.setup.table'));
        return data.data;
    }
</script>

<template>
    <Head :title="$t('menu.power_bi_mapping')" />

    <AppLayout :breadcrumbs="breadcrumbs">
        <PageCard>
            <DataTable
                :table-key="QueryKey.POWER_BI"
                :columns="columns"
                :data="data"
                :loading="isPending"
            >
                <template #title>
                    <CardTitle>
                        {{ $t('menu.power_bi_settings') }}
                    </CardTitle>
                </template>
                <template
                    #actions
                    v-if="hasPermission(Permission.POWERBI_SETUP_CREATE)"
                >
                    <DataTableToolbarActions name="power-bi-setup">
                        <CreateBIReport v-if="hasPermission(Permission.POWERBI_SETUP_CREATE)" />
                        <UploadBiReport v-if="hasPermission(Permission.POWERBI_SETUP_CREATE)" />
                    </DataTableToolbarActions>
                </template>
            </DataTable>
        </PageCard>
    </AppLayout>
</template>
